import { useRouteError, isRouteErrorResponse } from "react-router";

/**
 * Detects a lazy-chunk load failure. After a deploy, an already-loaded main.js can request a
 * chunk whose hash no longer exists on the server, throwing "ChunkLoadError" / "Loading chunk
 * N failed" / "error loading dynamically imported module". A fresh page load fetches the new
 * main.js with the correct chunk hashes, so the fix is simply to reload once.
 */
function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const name = error.name ?? "";
  const message = error.message ?? "";
  return name === "ChunkLoadError" || /Loading chunk [\w-]+ failed|error loading dynamically imported module|Failed to fetch dynamically imported module/i.test(message);
}

// Guards against a reload loop: if reloading doesn't resolve the error (e.g. the chunk is
// genuinely gone), we fall through to showing the error instead of reloading forever.
const RELOAD_FLAG = "chunkReloadAttempted";

export function ErrorBoundaryElement() {
  const error = useRouteError();

  if (isChunkLoadError(error)) {
    let alreadyTried = true;
    try {
      alreadyTried = sessionStorage.getItem(RELOAD_FLAG) === "1";
      if (!alreadyTried) {
        sessionStorage.setItem(RELOAD_FLAG, "1");
        window.location.reload();
      }
    } catch {
      // sessionStorage unavailable (private mode / disabled) — reload once without the guard.
      window.location.reload();
    }
    // While the reload is in flight (or if we've already tried once), render a lightweight notice.
    return (
      <div style={{ padding: 24 }}>
        <p>Updating to the latest version…</p>
      </div>
    );
  }

  if (isRouteErrorResponse(error)) {
    return (
      <div>
        <h1>
          {error.status} {error.statusText}
        </h1>
        <p>{error.data}</p>
      </div>
    );
  } else if (error instanceof Error) {
    return (
      <div>
        <h1>Error</h1>
        <p>{error.message}</p>
        <p>The stack trace is:</p>
        <pre>{error.stack}</pre>
      </div>
    );
  } else {
    return <h1>Unknown Error</h1>;
  }
}
